import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, EventEmitter, Input, Output, forwardRef, OnChanges, SimpleChanges, OnDestroy, AfterViewInit } from '@angular/core';
import {
  FormGroup,
  FormControl,
  Validators,
  FormBuilder,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Logger } from '@app/logging.service';
import { CustomerDetails } from '@app/shared/models/customer-details';
import { MatDialog } from '@angular/material/dialog';
import { AlertComponent } from '../alert/alert.component';
import { MatchResult, Organization } from '@app/shared/models/dnb';
import { CompanySearchResultsDialogComponent } from '../company-search-results-dialog/company-search-results-dialog.component';
import RequiredWhenValidator from '@app/shared/validator/required-when-validator';
import { CustomerType } from '@app/shared/models/customer-type';
import { ConfigService } from '@app/shared/services/config.service';
import LogRocket from 'logrocket';
import { Store } from '@ngrx/store';
import { AppState } from '@app/state/app.state';
import { ActivatedRoute } from '@angular/router';
import { Address } from '@app/shared/models/address';
import { Observable, Subscription } from 'rxjs';
import { SalesType } from '@app/shared/enums/sales-type';
import { CustomerTypeSize } from '@app/shared/models/customer-type-size';
import { SetAddress, SetCustomerType, SetCustomerTypeSize } from '@utility-information/state/actions/utility-information.actions';
import {
  customerTypeSelector,
  customerTypeSizeSelector,
  salesTypeSelector,
  stateSelector,
} from '@utility-information/state/selectors/utility-information.selectors';
import { ClearQuote, SetCustomerDetails } from '@quoting/state/actions/quoting-module.actions';
import { isReadOnlySelector } from '@app/state/app.selectors';
import { BillingType } from '@app/shared/enums/billing-type';

@Component({
  selector: 'app-customer-details',
  templateUrl: './customer-details.component.html',
  styleUrls: ['./customer-details.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomerDetailsComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CustomerDetailsComponent),
      multi: true,
    },
  ],
})
export class CustomerDetailsComponent implements OnInit, ControlValueAccessor, OnChanges, Validators, OnDestroy {
  @Input() customerDetails: CustomerDetails;
  @Input() dunsLookupEnabled: boolean;
  @Input() showValidation: boolean;  
  @Output() organisationNameChange = new EventEmitter<string>();
  @Output() formInitialised = new EventEmitter<FormGroup>();
  @Output() propagateAddressFound = new EventEmitter<Address>();
  @Input() showCustomerNameFields = true;
  onChange;
  onTouched;

  public frmCustomerDetails: FormGroup = new FormGroup({});
  public customerType = CustomerType;
  public customerTypeSize = CustomerTypeSize;
  private logger: Logger;
  private invokedLookup: boolean;
  quoteRequestId: any;
  routeSubscription$: any;
  customerType$: Observable<string>;
  customerTypeSize$: Observable<string>;
  usState$: Observable<string>;
  salesType$: Observable<string>;
  customerTypeSizeValidation: boolean;
  customerTypes: any[] = [
    {
      value: this.customerType.Residential,
      name: 'Residential',
    },
    {
      value: this.customerType.Commercial,
      name: 'Commercial',
    },
  ];

  customerTypeSizes: any[] = [
    {
      value: this.customerTypeSize.Small,
      name: 'Small Commercial',
    },
    {
      value: this.customerTypeSize.Large,
      name: 'Large Commercial',
    },
  ];
  private isReadOnly$: Subscription;
  isReadOnly: boolean;

  get customerIsCommercial(): boolean {
    if (!this.frmCustomerDetails?.get('customerType')?.value) return false;
  
    return this.frmCustomerDetails?.get('customerType')?.value === this.customerType.Commercial;
  }

  constructor(
    private fb: FormBuilder,
    private httpClient: HttpClient,
    private dialog: MatDialog,
    private configService: ConfigService,
    private store: Store<AppState>,
    private route: ActivatedRoute
  ) {
    this.logger = Logger.for(this);
    this.customerType$ = store.select(customerTypeSelector);
    this.customerTypeSize$ = store.select(customerTypeSizeSelector);
    this.salesType$ = store.select(salesTypeSelector);
    this.usState$ = store.select(stateSelector);
  }

  ngOnDestroy(): void {
    this.isReadOnly$?.unsubscribe();
    this.routeSubscription$?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      if (this.showValidation && changes?.customerDetails?.currentValue?.saleType !== SalesType.renewal) {
        this.frmCustomerDetails.markAllAsTouched();
      }
    }
  }

  get customerTypeCtrl() {
    return this.frmCustomerDetails?.get('customerType');
  }

  get customerTypeSizeCtrl() {
    return this.frmCustomerDetails?.get('customerTypeSize');
  }

  get organisationNameCtrl() {
    return this.frmCustomerDetails?.get('organisationName');
  }

  get customerNameCtrl() {
    return this.frmCustomerDetails?.get('customerName');
  }

   ngOnInit(): void {
    this.setControlForms();

    this.frmCustomerDetails.patchValue({
      ...this.customerDetails,
    });

    this.routeSubscription$ = this.route.params.subscribe((x) => {
      this.quoteRequestId = x.quoteRequestId;
      if (!isNaN(x.quoteRequestId)) {
        this.quoteRequestId = Number(x.quoteRequestId);
      } 
      else {
        this.store.dispatch(new ClearQuote());
      }
    });   

    this.isReadOnly$ = this.store.select(isReadOnlySelector).subscribe((isReadOnly) => {
      this.isReadOnly = isReadOnly;
      if (this.isReadOnly) {
        this.frmCustomerDetails.disable({ emitEvent: false });
      } 
    });
  }

  setControlForms() {
    // For both
    const customerType = new FormControl(CustomerType.Commercial, [Validators.required]);
    const location = new FormGroup({});

    // Residential controls
    const customerName = this.fb.group({
      customerFirstName: [
        '',
        [RequiredWhenValidator(customerType, CustomerType.Residential), Validators.minLength(1), Validators.maxLength(100)],
      ],
      customerLastName: [
        '',
        [RequiredWhenValidator(customerType, CustomerType.Residential), Validators.minLength(1), Validators.maxLength(100)],
      ],
    });

    // Commercial controls
    const organisationName = new FormControl('', [
      RequiredWhenValidator(customerType, CustomerType.Commercial),
      Validators.minLength(1),
      Validators.maxLength(255),
    ]);

    this.frmCustomerDetails = this.fb.group({
      customerType,
      customerTypeSize: [CustomerTypeSize.Small],
      organisationName,
      customerName,
      location,
      duns: [''],
      telephoneNumber: [''],
      isTaxExempt: [false],
    });
  }

  registerOnTouched(fn) {
    this.onTouched = fn;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  writeValue(value: any) {
    if (value) this.frmCustomerDetails?.patchValue(value);
  }

  changeFormData() {
    this.onChange(this.frmCustomerDetails?.getRawValue());
  }

  public customerTyperadioChanges(bt: any) {
    this.store.dispatch(new SetCustomerType(this.quoteRequestId, bt.value));
    if (bt.value === CustomerType.Residential) {
      this.customerTypeSizeCtrl.setValue(CustomerTypeSize.None);
      this.store.dispatch(new SetCustomerTypeSize(this.quoteRequestId, CustomerTypeSize.None));
    } else if (bt.value === CustomerType.Commercial) {
      this.customerTypeSizeCtrl.setValue(CustomerTypeSize.Small);
      this.store.dispatch(new SetCustomerTypeSize(this.quoteRequestId, CustomerTypeSize.Small));
    }
    this.changeFormData();
  }

  public customerTypeSizeradioChanges(bt: any) {
    this.changeFormData();
    this.store.dispatch(new SetCustomerTypeSize(this.quoteRequestId, bt.value));
  }

  public searchCompany(): void {
    this.invokedLookup = true;
    const url = this.configService.getApiPath('companySearch/search');
    this.httpClient.post(url, { name: this.frmCustomerDetails.get('organisationName').value, zipCode: this.getZipCode() }).subscribe(
      (res: MatchResult) => {
        this.invokedLookup = false;
        LogRocket.track('feature:duns_lookup');

        const dialogRef = this.dialog.open(CompanySearchResultsDialogComponent, {
          data: res,
        });

        dialogRef.afterClosed().subscribe((selectedCompany: Organization) => {
          if (selectedCompany) {          
            this.frmCustomerDetails.get('organisationName').setValue(selectedCompany.primaryName);
            this.frmCustomerDetails.get('duns').setValue(selectedCompany.duns);

            const telephoneNumber = selectedCompany.telephone?.[0]?.telephoneNumber;
            this.frmCustomerDetails.get('telephoneNumber').setValue(telephoneNumber);

            const firstSeniorPrincipalName = selectedCompany.mostSeniorPrincipals?.[0]?.fullName?.split(/\s+/);
            if (firstSeniorPrincipalName && firstSeniorPrincipalName?.length > 0) {
              this.frmCustomerDetails.get('customerName').patchValue({
                customerFirstName: firstSeniorPrincipalName?.[0],
                customerLastName: firstSeniorPrincipalName?.[firstSeniorPrincipalName.length - 1],
              });
            }

            var location = this.frmCustomerDetails.get('location');

            location.patchValue(
              {
                addressLine1: selectedCompany.primaryAddress.streetAddress.line1,
                addressLine2: selectedCompany.primaryAddress.streetAddress.line2,
                city: selectedCompany.primaryAddress.addressLocality.name,
                state: selectedCompany.primaryAddress.addressRegion.abbreviatedName,
                country: selectedCompany.primaryAddress.addressCountry.isoAlpha2Code,
                zipCode: selectedCompany.primaryAddress.postalCode,
              },
              { emitEvent: true }
            );

            /* This triggers the events required for the address to be updated in the main form even if the zip code remains the same when the
            user selects a company in the popup */
            this.addressFound({
              zipCode: selectedCompany.primaryAddress.postalCode,
              city: selectedCompany.primaryAddress.addressLocality.name,
              state: selectedCompany.primaryAddress.addressRegion.abbreviatedName,
              //MunicipalityCode is not relevant here
              municipalityCode: '',
            });

            this.store.dispatch(new SetAddress(this.quoteRequestId, location.get('zipCode').value, location.get('state').value));
            
            location.markAsDirty();

            this.frmCustomerDetails.get('location').markAsPristine();
            this.frmCustomerDetails.get('organisationName').markAsPristine();

            this.store.dispatch(new SetCustomerDetails(this.quoteRequestId, {
              duns :selectedCompany.duns,
              organisationName: selectedCompany.primaryName,
              telephoneNumber: this.frmCustomerDetails.get('telephoneNumber').value,
              billingType: "",
              customerName: this.frmCustomerDetails.get('customerName').value,
              customerType: this.frmCustomerDetails.get('customerType').value,
              isTaxExempt: this.frmCustomerDetails.get('isTaxExempt').value,
              location: this.frmCustomerDetails.get('location').value,
              customerTypeSize: this.frmCustomerDetails.get('customerTypeSize').value
            }));
          }
        });
      },
      (err: HttpErrorResponse) => {
        this.invokedLookup = false;
        if (err.status === 404) {
          this.dialog.open(AlertComponent, {
            data: {
              title: 'No Results Found',
              message: 'Please try another search criteria.',
            },
          });
        } else {
          this.logger.error(err);
          this.dialog.open(AlertComponent, {
            data: {
              title: 'We Could Not Perform a Company Search at This Time',
              message: 'Please try again later.',
            },
          });
        }
      }
    );
  }

  public addressFound(address: Address) {
    this.changeFormData();
    this.propagateAddressFound.emit(address);
  }

  public validForLookup(): boolean {
    return (
      !this.invokedLookup &&
      this.frmCustomerDetails.get('organisationName').valid &&
      this.frmCustomerDetails.get('location').valid &&
      (this.frmCustomerDetails.get('location').dirty || this.frmCustomerDetails.get('organisationName').dirty)
    );
  }

  public zipLookupInitialised(frm: FormGroup) {
    this.frmCustomerDetails.setControl('location', frm);

    // Tell parent component this form has also finished initialising
    this.formInitialised.emit(this.frmCustomerDetails);
  }

  private getZipCode() {
    return this.frmCustomerDetails.getRawValue().location.zipCode;
  }

  validate(control: AbstractControl): ValidationErrors {
    let salesType;
    this.salesType$
      .subscribe((x) => {
        salesType = x;
      })
      .unsubscribe();
    if (salesType === SalesType.renewal) {
      return null;
    } else {
      if (this.customerTypeCtrl.value == this.customerType.Commercial) {
        if (this.organisationNameCtrl.valid) {
          return null;
        } else {
          return { invalidFrom: { valid: false, message: 'Company Name Invalid' } };
        }
      } else {
        if (this.customerNameCtrl.valid) {
          return null;
        } else {
          return { invalidFrom: { valid: false, message: 'Customer Name Invalid' } };
        }
      }
    }
  }
}
